import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { confirmAlert } from "react-confirm-alert";
import AlunoForm from "./Form/AlunoForm";
import AlunoTable from "./AlunoTable";
import { page, remove, save, findByCodigo, findByNome, findAlunoByUsuarioEmail } from "./api-aluno";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../util/ToastUtil";
import Container from "../UI/Container";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

const BUSCAS = [
  { value: "nome", label: "Nome", request: findByNome },
  { value: "codigo", label: "Código", request: findByCodigo },
  { value: "email", label: "E-mail", request: findAlunoByUsuarioEmail },
];

const SAVE_ERRORS = {
  "Erro: CodAlunoEscola already in use": "Código já utilizado por outro aluno.",
  "User already registered for a Student": "Usuário já registrado para outro aluno.",
};

const toList = (data) => (Array.isArray(data) ? data : data?.codigo ? [data] : []);

export default function AlunoContainer() {
  const [aluno, setAluno] = useState(null);
  const [alunos, setAlunos] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [buscaPor, setBuscaPor] = useState("nome");
  const [termo, setTermo] = useState("");
  // Resultado da busca: null quando a lista paginada está sendo exibida.
  const [resultados, setResultados] = useState(null);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    page(pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setAlunos(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [pageNumber, reloadKey]);

  const handleBuscar = (event) => {
    event.preventDefault();
    const valor = termo.trim();
    if (!valor) {
      setResultados(null);
      return;
    }
    const { request } = BUSCAS.find((b) => b.value === buscaPor);
    setIsLoading(true);
    request(valor)
      .then((data) => {
        if (data?.error) {
          errorMessage(data.message);
          return;
        }
        setResultados(toList(data));
      })
      .finally(() => setIsLoading(false));
  };

  const limparBusca = () => {
    setTermo("");
    setResultados(null);
  };

  const updateAlunoState = (data) => {
    const replace = (lista) => lista.map((a) => (a.codigo === data.codigo ? data : a));
    setAlunos((prevState) => ({ ...prevState, content: replace(prevState.content) }));
    setResultados((prevState) => (prevState ? replace(prevState) : prevState));
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!aluno.nome?.trim()) {
      errorToast("Informe o nome do aluno.");
      return;
    }
    const eNovo = aluno.eNovo;
    setIsSaving(true);
    save(aluno)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(SAVE_ERRORS[data.message] ?? data.message);
          return;
        }
        success("Salvo com sucesso.");
        setAluno(data);
        if (eNovo) {
          setPageNumber(0);
          setReloadKey((key) => key + 1);
        } else {
          updateAlunoState(data);
        }
      })
      .finally(() => setIsSaving(false));
  };

  const removeAluno = () => {
    const codigo = aluno.codigo;
    setIsSaving(true);
    remove(codigo)
      .then((data) => {
        if (data?.error) {
          errorMessage(
            "O aluno não pode ser removido pois possui turmas cadastradas. Remova todas as turmas do aluno antes.",
          );
          return;
        }
        setAluno(null);
        setResultados((prevState) => (prevState ? prevState.filter((a) => a.codigo !== codigo) : prevState));
        // Se o item removido era o único da página, volta uma página.
        if (alunos.content.length === 1 && pageNumber > 0) {
          setPageNumber((p) => p - 1);
        } else {
          setReloadKey((key) => key + 1);
        }
        success("Aluno removido.");
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = () => {
    confirmAlert({
      title: "Alerta",
      message: "Deseja remover este aluno?",
      buttons: [
        { label: "Sim", onClick: removeAluno },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const handleDeleteUsuario = () => {
    confirmAlert({
      title: "Alerta",
      message: "Deseja remover o vínculo do aluno com este usuário? A alteração vale após salvar.",
      buttons: [
        { label: "Sim", onClick: () => setAluno((prevState) => ({ ...prevState, usuarioAluno: null })) },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  if (aluno) {
    return (
      <Container>
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
          <Tooltip title="Voltar para a lista">
            <IconButton edge="start" onClick={() => setAluno(null)}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box minWidth={0}>
            <Typography variant="h6" noWrap>
              {aluno.eNovo ? "Novo Aluno" : aluno.nome || "Aluno"}
            </Typography>
            {!aluno.eNovo && aluno.codAlunoEscola && (
              <Typography variant="body2" color="text.secondary">
                Código {aluno.codAlunoEscola}
              </Typography>
            )}
          </Box>
        </Stack>
        <AlunoForm
          aluno={aluno}
          setAluno={setAluno}
          clickSubmit={clickSubmit}
          handleDelete={handleDelete}
          handleDeleteUsuario={handleDeleteUsuario}
          isSaving={isSaving}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6">Alunos</Typography>
          <Typography variant="body2" color="text.secondary">
            Cadastro de alunos, turmas e responsáveis.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAluno({ eNovo: true })}>
          Novo Aluno
        </Button>
      </Stack>

      <Stack component="form" onSubmit={handleBuscar} direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2}>
        <TextField
          select
          size="small"
          label="Buscar por"
          value={buscaPor}
          onChange={(event) => setBuscaPor(event.target.value)}
          sx={{ minWidth: 140 }}
        >
          {BUSCAS.map((b) => (
            <MenuItem key={b.value} value={b.value}>
              {b.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          fullWidth
          placeholder={`Digite o ${BUSCAS.find((b) => b.value === buscaPor).label.toLowerCase()} e pressione Enter`}
          value={termo}
          onChange={(event) => setTermo(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: termo && (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Limpar busca" onClick={limparBusca}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Button type="submit" variant="outlined" sx={{ flexShrink: 0 }}>
          Buscar
        </Button>
      </Stack>

      {resultados && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {resultados.length} resultado(s) para "{termo.trim()}".{" "}
          <Button size="small" onClick={limparBusca}>
            Ver todos
          </Button>
        </Typography>
      )}

      <AlunoTable
        alunos={resultados ?? alunos.content}
        pagination={resultados ? null : alunos}
        handleChangePage={(event, newPage) => setPageNumber(newPage)}
        setAluno={setAluno}
        isLoading={isLoading}
        emptyText={resultados ? "Nenhum aluno encontrado." : "Nenhum aluno cadastrado."}
      />
    </Container>
  );
}
